import { useEffect, useMemo, useState } from 'react';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { IntlProvider } from 'react-intl';
import { Header } from '../components/Header.tsx';
import { Footer } from '../components/Footer.tsx';
import { SalesContactProvider } from '../components/SalesContact.tsx';
import { WhatsAppFab } from '../components/WhatsAppFab.tsx';
import { useLocaleMeta } from '../lib/useDocumentMeta.ts';
import {
  FALLBACK_LOCALE,
  detectInitialLocale,
  messagesByLocale,
  parseLocaleParam,
  persistLocale,
  type Locale,
} from '../intl/index.ts';

function RootLayout() {
  const { lang } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [locale, setLocale] = useState<Locale>(() => detectInitialLocale(lang));

  // Merge over the EN source so any missing pt-BR key falls back to English
  // instead of rendering the raw message id.
  const messages = useMemo(
    () => ({ ...messagesByLocale[FALLBACK_LOCALE], ...messagesByLocale[locale] }),
    [locale],
  );

  // Keep <html lang> in step with the active locale for a11y + SEO.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Locale-dependent baseline meta (og:locale). Per-route title/description
  // live in each route via useRouteMeta.
  useLocaleMeta(messages);

  // A shared ?lang= link (or someone hand-editing the URL) drives the language.
  useEffect(() => {
    if (lang && lang !== locale) {
      setLocale(lang);
      persistLocale(lang);
    }
  }, [lang]);

  function onLocaleChange(next: Locale): void {
    setLocale(next);
    persistLocale(next);
    // Reflect the choice in the URL so the page stays shareable in this
    // language; replace (not push) so toggling doesn't pile up history entries.
    void navigate({ search: (prev) => ({ ...prev, lang: next }), replace: true });
  }

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={FALLBACK_LOCALE}
      messages={messages}
      onError={() => {
        /* pt-BR is at parity; suppress any residual dev noise */
      }}
    >
      <SalesContactProvider>
        <div className="flex min-h-full flex-col">
          <Header locale={locale} onLocaleChange={onLocaleChange} />
          <main className="flex-1">
            <Outlet />
          </main>
          <Footer />
        </div>
        <WhatsAppFab />
      </SalesContactProvider>
    </IntlProvider>
  );
}

export const Route = createRootRoute({
  // `?lang=` is validated once here so every route (and Route.useSearch) sees a
  // typed, normalized Locale — unknown/garbage values are simply dropped.
  validateSearch: (search: Record<string, unknown>): { lang?: Locale } => {
    const lang = parseLocaleParam(typeof search.lang === 'string' ? search.lang : null);
    return lang ? { lang } : {};
  },
  component: RootLayout,
});
